import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuth from './useAuth';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import Footer from './Footer';
import Unauthorized from '../pages/Unauthorized';
import PointageService from './PointageService';
import GlobalMessagingWidget from './GlobalMessagingWidget';

const Layout = ({ children, fullScreen = false }) => {
  if (fullScreen) {
    return <div className="pegasus-fullscreen-route">{children}</div>;
  }

  return (
    <div className="wrapper pegasus-layout">
      <Sidebar />
      <div className="main pegasus-main">
        <Navbar />
        <main className="content">
          <div className="container-fluid p-0">{children}</div>
        </main>
        <GlobalMessagingWidget />
        <Footer />
      </div>
    </div>
  );
};

const PrivateRoute = ({ children, allowedRoles = [], fullScreen = false }) => {
  const location = useLocation();
  const { isAuthenticated, roles, isLoading } = useAuth();
  const [entryStatus, setEntryStatus] = useState({ isChecking: false, hasEntry: true });

  const isCarrefourUser = roles.includes('carrefour');

  useEffect(() => {
    let isMounted = true;
    const currentUserId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userId') : null;
    const shouldCheckEntry = isAuthenticated && isCarrefourUser && Boolean(currentUserId);

    if (!shouldCheckEntry) {
      setEntryStatus({ isChecking: false, hasEntry: true });
      return undefined;
    }

    const checkEntryStatus = async () => {
      setEntryStatus((prev) => ({ ...prev, isChecking: true }));
      try {
        const response = await PointageService.getTodayEntryStatus(currentUserId);
        if (isMounted) {
          setEntryStatus({ isChecking: false, hasEntry: Boolean(response?.hasEntry) });
        }
      } catch (error) {
        console.error('Unable to verify today\'s pointage entry', error);
        if (isMounted) {
          setEntryStatus({ isChecking: false, hasEntry: true });
        }
      }
    };

    checkEntryStatus();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, isCarrefourUser, location.pathname]);

  const hasPermission = useMemo(() => {
    if (!allowedRoles || allowedRoles.length === 0) {
      return true;
    }

    return allowedRoles.some((role) => roles.includes(role));
  }, [allowedRoles, roles]);

  const shouldForcePointageEntry = useMemo(() => {
    if (!isAuthenticated || !isCarrefourUser) {
      return false;
    }

    return location.pathname !== '/pointage' && !entryStatus.hasEntry;
  }, [entryStatus.hasEntry, isAuthenticated, isCarrefourUser, location.pathname]);

  if (isLoading) {
    return null;
  }

  if (entryStatus.isChecking && isAuthenticated && isCarrefourUser) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (shouldForcePointageEntry) {
    return <Navigate to="/pointage" replace state={{ from: location }} />;
  }

  const content = hasPermission ? children : <Unauthorized />;

  return <Layout fullScreen={fullScreen}>{content}</Layout>;
};

export default PrivateRoute;
